import {
  AlertDialog,
  Button,
  Card,
  cn,
  Dropdown,
  Label,
  ScrollShadow,
  Spinner,
  toast,
} from '@heroui/react';
/**
 * 作品页：「最近」自适应高度；「分类」flex-1 占满余下区域；横向列表双击进入编辑
 */
import { useState, useCallback, useRef, useMemo, useEffect, type Key } from 'react';
import { useNavigate } from 'react-router-dom';
import { CanvasThumbnailPreview } from '@/components/canvas/designer/thumbnails/CanvasThumbnailPreview';
import { parseCanvasData } from '@/components/canvas/document/validation';
import { formatDateTime, formatRelativeTime } from '@hs/shared/utils/formatTime';
import { IconChevronRight, IconDotsFilled, IconEdit, IconTrash } from '@tabler/icons-react';
import { DocThumbnailPreview } from '@/components/RichTextEditor/DocThumbnailPreview';
import {
  buildInitialCanvasContent,
  DEFAULT_SLIDE_SIZE,
} from '@/components/canvas/document/buildInitialCanvasContent';
import { trpc } from '@/lib/trpc';
import { KnowledgeBaseHeader, type KnowledgeBasesScope } from './components/KnowledgeBaseHeader';
import type { Page as PageType } from '@/components/canvas/document/publicTypes';
import { RacDialogTriggerAnchor } from '@/components/RacDialogTriggerAnchor';

const LIST_THUMB_WIDTH = 240;
const LIST_THUMB_HEIGHT = 135;

type KnowledgeBaseItem = {
  id: string;
  title?: string | null;
  content?: unknown;
  meta?: unknown;
  coverImage?: string | null;
  fileFormat?: 'slides' | 'doc' | null;
  createdAt?: string | null;
  updatedAt?: string | null;
};

function getFirstPageFromContent(item: KnowledgeBaseItem): PageType | null {
  const raw = item.content;
  if (raw == null || typeof raw !== 'object') return null;
  const parsed = parseCanvasData(raw);
  if (!parsed?.pages?.length) return null;
  return parsed.pages[0] as PageType;
}

/** 分类卡片扇形区：单张 16:9，尺寸随容器；ResizeObserver 驱动 Doc/Canvas 缩略图 */
function CategoryFanThumbLayer({
  item,
  className,
  onOpen,
}: {
  item: KnowledgeBaseItem;
  className?: string;
  onOpen: () => void;
}) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [dims, setDims] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const update = () => {
      const w = el.clientWidth;
      const h = el.clientHeight;
      if (w > 0 && h > 0) setDims((p) => (p.width === w && p.height === h ? p : { width: w, height: h }));
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const firstPage = item.fileFormat === 'slides' ? getFirstPageFromContent(item) : null;

  return (
    <div
      ref={wrapRef}
      role="button"
      tabIndex={0}
      className={cn(
        'pointer-events-auto cursor-pointer overflow-hidden rounded-lg border border-divider bg-muted shadow-sm outline-none transition-[transform,box-shadow] hover:shadow-md',
        className,
      )}
      onDoubleClick={(e) => {
        e.stopPropagation();
        onOpen();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        }
      }}
    >
      {dims.width > 0 && dims.height > 0 ? (
        item.fileFormat === 'doc' ? (
          <DocThumbnailPreview
            className="pointer-events-none size-full"
            width={dims.width}
            height={dims.height}
            documentTitle={item.title?.trim() ? item.title : '未命名'}
            documentContent={item.content ?? null}
            meta={item.meta}
            coverImage={typeof item.coverImage === 'string' ? item.coverImage : undefined}
            editorKey={`fan-${item.id}-${item.updatedAt ?? ''}`}
            scaleFactor={1}
            viewportPaddingPx={{ top: 6, left: 6, right: 6 }}
            richTextMinHeightClass="min-h-[72px]"
          />
        ) : firstPage ? (
          <CanvasThumbnailPreview
            page={firstPage}
            width={dims.width}
            height={dims.height}
            fit="cover"
            className="pointer-events-none size-full"
          />
        ) : (
          <div className="flex size-full items-center justify-center text-muted-foreground text-[10px]">
            无预览
          </div>
        )
      ) : null}
    </div>
  );
}

function CategoryLatestCard({
  category,
  items,
  onOpenKb,
}: {
  category: { id?: string | null; name?: string | null; updatedAt?: string | null };
  items: KnowledgeBaseItem[];
  onOpenKb: (item: KnowledgeBaseItem) => void;
}) {
  const thumbs = useMemo(
    () =>
      items
        .filter((i) => i.fileFormat === 'doc' || i.fileFormat === 'slides')
        .slice(0, 3),
    [items],
  );
  const [first, second, third] = [thumbs[0], thumbs[1], thumbs[2]];
  const updated = category.updatedAt ?? '';
  const timeStr = typeof updated === 'string' && updated ? formatRelativeTime(updated) : '—';

  return (
    <Card>
      <Card.Content className="flex min-h-0 flex-1 flex-col gap-2">
        <div className="flex shrink-0 items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="truncate text-base font-semibold leading-tight">
              {category.name?.trim() ? category.name : '未命名分类'}
            </div>
            <div
              className="mt-0.5 text-xs text-muted-foreground"
              title={typeof updated === 'string' && updated ? formatDateTime(updated) : undefined}
            >
              {timeStr}
            </div>
          </div>
          <div className="shrink-0" onPointerDown={(e) => e.stopPropagation()}>
            <Dropdown>
              <Button
                type="button"
                variant="ghost"
                isIconOnly
                size="sm"
                className="size-8 shrink-0 text-muted-foreground"
                aria-label="分类菜单"
              >
                <IconDotsFilled className="size-4" stroke={1.5} />
              </Button>
              <Dropdown.Popover className="p-0" placement="bottom end" offset={4}>
                <Dropdown.Menu aria-label="分类操作" />
              </Dropdown.Popover>
            </Dropdown>
          </div>
        </div>

        <div className="relative isolate mt-1 min-h-[132px] w-full flex-1 sm:min-h-[148px]">
          {second ? (
            <CategoryFanThumbLayer
              item={second}
              onOpen={() => onOpenKb(second)}
              className={cn(
                'absolute bottom-0 left-0 z-20 w-[54%] origin-bottom-left',
                'aspect-video -rotate-[11deg] hover:z-40',
              )}
            />
          ) : null}
          {third ? (
            <CategoryFanThumbLayer
              item={third}
              onOpen={() => onOpenKb(third)}
              className={cn(
                'absolute bottom-0 right-0 z-20 w-[54%] origin-bottom-right',
                'aspect-video rotate-[11deg] hover:z-40',
              )}
            />
          ) : null}
          {first ? (
            <CategoryFanThumbLayer
              item={first}
              onOpen={() => onOpenKb(first)}
              className="absolute left-1/2 top-0 z-30 w-[60%] -translate-x-1/2 aspect-video"
            />
          ) : (
            <div className="flex h-full min-h-[100px] items-center justify-center rounded-lg border border-dashed border-divider text-muted-foreground text-xs">
              暂无文档或幻灯片
            </div>
          )}
        </div>
      </Card.Content>
    </Card>
  );
}

function KnowledgeBaseCardToolbar({
  item,
  onEdit,
  onDelete,
}: {
  item: KnowledgeBaseItem;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const updated = item.updatedAt ?? item.createdAt ?? '';
  const timeStr =
    typeof updated === 'string' && updated ? formatRelativeTime(updated) : '—';

  return (
    <div className="flex min-w-0 items-center justify-between gap-1">
      <span
        className="min-w-0 flex-1 truncate text-xs text-muted-foreground"
        title={typeof updated === 'string' && updated ? formatDateTime(updated) : undefined}
      >
        {timeStr}
      </span>
      <div
        className="shrink-0"
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <Dropdown>
          <Button
            type="button"
            variant="ghost"
            isIconOnly
            size="sm"
            className="size-7 shrink-0 text-muted-foreground"
            aria-label="更多操作"
          >
            <IconDotsFilled className="size-4" stroke={1.5} />
          </Button>
          <Dropdown.Popover className="min-w-32" placement="bottom end" offset={4}>
            <Dropdown.Menu>
              <Dropdown.Item id="edit" textValue="编辑" onAction={() => onEdit()}>
                <Label className="flex items-center gap-2 font-normal">
                  <IconEdit className="size-4 shrink-0" stroke={1.5} />
                  编辑
                </Label>
              </Dropdown.Item>
              <Dropdown.Item id="delete" textValue="删除" variant="danger" onAction={() => onDelete()}>
                <Label className="flex items-center gap-2 font-normal">
                  <IconTrash className="size-4 shrink-0" stroke={1.5} />
                  删除
                </Label>
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown.Popover>
        </Dropdown>
      </div>
    </div>
  );
}

export default function WorksPage() {
  const navigate = useNavigate();
  const [deleteTarget, setDeleteTarget] = useState<KnowledgeBaseItem | null>(null);
  const [deleteTitleLabel, setDeleteTitleLabel] = useState('');
  const deleteTargetRef = useRef<KnowledgeBaseItem | null>(null);
  const [scope, setScope] = useState<KnowledgeBasesScope>('my');

  const utils = trpc.useUtils();
  const createDocMutation = trpc.knowledgeBases.create.useMutation();
  const createSlidesMutation = trpc.knowledgeBases.create.useMutation();
  const deleteMutation = trpc.knowledgeBases.delete.useMutation({
    onSuccess: async () => {
      await Promise.all([
        utils.knowledgeBases.listMy.invalidate(),
        utils.knowledgeBases.list.invalidate(),
        utils.knowledgeBases.listLatestCategoryDetails.invalidate(),
      ]);
      toast.success('已删除');
    },
    onError: () => {
      toast.danger('删除失败');
    },
  });

  const listQueryBase = {
    sortBy: 'updatedAt' as const,
    sortOrder: 'desc' as const,
    where: undefined,
  };

  const recentListInputMy = useMemo(
    () => ({
      ...listQueryBase,
      page: 1,
      pageSize: 80,
    }),
    [],
  );
  const recentListInputShared = useMemo(
    () => ({
      ...listQueryBase,
      page: 1,
      pageSize: 80,
      isSharedToSpace: true as const,
    }),
    [],
  );

  const listMyRecentQuery = trpc.knowledgeBases.listMy.useQuery(recentListInputMy, {
    enabled: scope === 'my',
  });
  const listSharedRecentQuery = trpc.knowledgeBases.list.useQuery(recentListInputShared, {
    enabled: scope === 'shared',
  });

  const recentQuery = scope === 'my' ? listMyRecentQuery : listSharedRecentQuery;
  const recentData = recentQuery.data;
  /** 仅首屏无缓存时展示 loading；后台 refetch 保留已有列表，避免新建等操作闪一下 */
  const showRecentLoading = recentQuery.isPending && !recentQuery.data;

  const recentItems = useMemo(() => {
    const items = (recentData?.items ?? []) as KnowledgeBaseItem[];
    return items.filter((i) => i.fileFormat === 'doc' || i.fileFormat === 'slides').slice(0, 20);
  }, [recentData?.items]);

  const categoryLatestInput = useMemo(
    () => ({
      categoryCount: 8,
      knowledgebaseCount: 3,
      isSpaceCategory: scope === 'shared',
    }),
    [scope],
  );

  const categoryLatestQuery = trpc.knowledgeBases.listLatestCategoryDetails.useQuery(categoryLatestInput);

  const openNewSlidesCanvas = useCallback(async () => {
    try {
      const data = await createSlidesMutation.mutateAsync({
        title: '未命名',
        fileFormat: 'slides',
        content: buildInitialCanvasContent(DEFAULT_SLIDE_SIZE),
      });
      navigate(`/knowledge-bases/slides/${data.id}`);
    } catch {
      toast.danger('创建幻灯片失败');
    }
  }, [createSlidesMutation, navigate]);

  const handleOpenCanvas = useCallback(
    (media: KnowledgeBaseItem) => {
      navigate(`/knowledge-bases/slides/${media.id}`);
    },
    [navigate],
  );

  const createNewDocAndNavigate = useCallback(async () => {
    try {
      const data = await createDocMutation.mutateAsync({
        title: '未命名',
        fileFormat: 'doc',
        content: {},
      });
      navigate(`/knowledge-bases/doc/${data.id}`);
    } catch {
      toast.danger('创建文档失败');
    }
  }, [createDocMutation, navigate]);

  const handleOpenDoc = useCallback(
    (doc: KnowledgeBaseItem) => {
      navigate(`/knowledge-bases/doc/${doc.id}`);
    },
    [navigate],
  );

  const openCategoryKb = useCallback(
    (item: KnowledgeBaseItem) => {
      if (item.fileFormat === 'doc') handleOpenDoc(item);
      else if (item.fileFormat === 'slides') handleOpenCanvas(item);
    },
    [handleOpenDoc, handleOpenCanvas],
  );

  const handleScopeSegmentChange = useCallback((key: Key) => {
    const k = String(key) as KnowledgeBasesScope;
    if (k === 'my' || k === 'shared') setScope(k);
  }, []);

  const requestDeleteKnowledgeBase = useCallback((item: KnowledgeBaseItem) => {
    deleteTargetRef.current = item;
    setDeleteTitleLabel(item.title?.trim() ? item.title.trim() : '未命名');
    setDeleteTarget(item);
  }, []);

  const handleDeleteDialogOpenChange = useCallback((open: boolean) => {
    if (!open) {
      deleteTargetRef.current = null;
      setDeleteTitleLabel('');
      setDeleteTarget(null);
    }
  }, []);

  const confirmDeleteKnowledgeBase = useCallback(async () => {
    const item = deleteTargetRef.current;
    if (!item) return;
    try {
      await deleteMutation.mutateAsync({ id: item.id });
      deleteTargetRef.current = null;
      setDeleteTitleLabel('');
      setDeleteTarget(null);
    } catch {
      /* toast in mutation onError */
    }
  }, [deleteMutation]);

  return (
    <>
      <AlertDialog isOpen={deleteTarget !== null} onOpenChange={handleDeleteDialogOpenChange}>
        <RacDialogTriggerAnchor />
        <AlertDialog.Backdrop>
          <AlertDialog.Container placement="center">
            <AlertDialog.Dialog>
              <AlertDialog.Header>
                <AlertDialog.Heading>删除确认</AlertDialog.Heading>
              </AlertDialog.Header>
              <AlertDialog.Body>
                确定要删除「{deleteTitleLabel}」吗？删除后将不可恢复。
              </AlertDialog.Body>
              <AlertDialog.Footer>
                <Button variant="secondary" onPress={() => handleDeleteDialogOpenChange(false)}>
                  取消
                </Button>
                <Button
                  variant="danger"
                  isDisabled={deleteMutation.isPending}
                  onPress={() => void confirmDeleteKnowledgeBase()}
                >
                  {deleteMutation.isPending ? '删除中…' : '删除'}
                </Button>
              </AlertDialog.Footer>
            </AlertDialog.Dialog>
          </AlertDialog.Container>
        </AlertDialog.Backdrop>
      </AlertDialog>

      <div className="flex h-full min-h-0 flex-col">
        <KnowledgeBaseHeader
          scope={scope}
          onScopeChange={handleScopeSegmentChange}
          onNewDoc={() => void createNewDocAndNavigate()}
          onNewSlides={openNewSlidesCanvas}
        />

        <div className="flex min-h-0 flex-1 flex-col">
          <section className="shrink-0 border-b border-divider p-0">
            <div className="flex h-12 shrink-0 items-center justify-between gap-2 px-4">
              <h2 className="m-0 text-base font-medium">最近</h2>
              <Button
                variant="ghost"
                className="shrink-0 gap-1 font-normal"
                onPress={() => navigate('/knowledge-bases/list')}
              >
                查看全部
                <IconChevronRight className="size-4 shrink-0" stroke={1.5} />
              </Button>
            </div>
            <div className="min-w-0 px-4 pb-3">
              {showRecentLoading ? (
                <div className="flex min-h-[180px] items-center justify-center">
                  <Spinner className="size-8" aria-label="加载中" />
                </div>
              ) : recentItems.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                  <p className="mb-3 text-sm">暂无最近内容</p>
                  <div className="flex flex-wrap items-center justify-center gap-2">
                    <Button variant="primary" size="sm" onPress={() => void createNewDocAndNavigate()}>
                      新建文档
                    </Button>
                    <Button variant="secondary" size="sm" onPress={openNewSlidesCanvas}>
                      新建幻灯片
                    </Button>
                  </div>
                </div>
              ) : (
                <ScrollShadow orientation="horizontal" className="w-full max-w-full pb-1" hideScrollBar>
                  <div className="flex w-max flex-nowrap items-stretch gap-4 py-1">
                    {recentItems.map((item) => {
                      const firstPage =
                        item.fileFormat === 'slides' ? getFirstPageFromContent(item) : null;
                      return (
                        <div key={item.id} className="flex w-[240px] shrink-0 flex-col gap-2">
                          {item.fileFormat === 'doc' ? (
                            <DocThumbnailPreview
                              role="button"
                              tabIndex={0}
                              className="cursor-pointer rounded-lg border border-divider transition-shadow hover:shadow-md"
                              onDoubleClick={() => handleOpenDoc(item)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  handleOpenDoc(item);
                                }
                              }}
                              width={LIST_THUMB_WIDTH}
                              height={LIST_THUMB_HEIGHT}
                              documentTitle={item.title?.trim() ? item.title : '未命名'}
                              documentContent={item.content ?? null}
                              meta={item.meta}
                              coverImage={
                                typeof item.coverImage === 'string' ? item.coverImage : undefined
                              }
                              editorKey={`${item.id}-${item.updatedAt ?? ''}`}
                              scaleFactor={1}
                              viewportPaddingPx={{ top: 8, left: 8, right: 8 }}
                              richTextMinHeightClass="min-h-[120px]"
                            />
                          ) : (
                            <div
                              role="button"
                              tabIndex={0}
                              className="relative shrink-0 cursor-pointer overflow-hidden rounded-lg border border-divider bg-muted transition-shadow hover:shadow-md"
                              style={{
                                width: LIST_THUMB_WIDTH,
                                height: LIST_THUMB_HEIGHT,
                              }}
                              onDoubleClick={() => handleOpenCanvas(item)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  handleOpenCanvas(item);
                                }
                              }}
                            >
                              {firstPage ? (
                                <CanvasThumbnailPreview
                                  page={firstPage}
                                  width={LIST_THUMB_WIDTH}
                                  height={LIST_THUMB_HEIGHT}
                                  fit="cover"
                                  className="size-full"
                                />
                              ) : (
                                <div className="flex size-full items-center justify-center text-muted-foreground text-xs">
                                  无预览
                                </div>
                              )}
                            </div>
                          )}
                          <div className="w-full min-w-0 shrink-0">
                            <KnowledgeBaseCardToolbar
                              item={item}
                              onEdit={() =>
                                item.fileFormat === 'doc'
                                  ? handleOpenDoc(item)
                                  : handleOpenCanvas(item)
                              }
                              onDelete={() => requestDeleteKnowledgeBase(item)}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </ScrollShadow>
              )}
            </div>
          </section>

          <section className="flex min-h-0 flex-1 flex-col border-b border-border p-0">
            <div className="flex h-12 shrink-0 items-center px-4">
              <h2 className="m-0 text-base font-medium">分类</h2>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
              {categoryLatestQuery.isPending && !categoryLatestQuery.data ? (
                <div className="flex min-h-[200px] items-center justify-center">
                  <Spinner className="size-8" aria-label="加载分类" />
                </div>
              ) : !categoryLatestQuery.data?.length ? (
                <div className="flex min-h-[120px] flex-col items-center justify-center py-8 text-center text-muted-foreground text-sm">
                  暂无最近更新的分类
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-4">
                  {categoryLatestQuery.data.map((row, idx) => (
                    <CategoryLatestCard
                      key={row.id ?? `category-${idx}`}
                      category={row}
                      items={(row.items ?? []) as KnowledgeBaseItem[]}
                      onOpenKb={openCategoryKb}
                    />
                  ))}
                </div>
              )}
            </div>
          </section>
        </div>
      </div>

    </>
  );
}
